import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Button,
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Skeleton,
} from '@monksflow/monks-ui';
import { ChartColumnIncreasing, MoreVertical, Plus, TrendingUp } from 'lucide-react';
import type { ContentTestListItemDto } from '@inference.engine/persona-api-client';

import { Loading, ToolbarSkeleton } from '@/shared/ui';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import { ContentToolbar, OwnerCell, type OwnerInfo, PaginationControls } from '@/shared/ui';
import { EmptyState } from '@/shared/ui/EmptyState';
import { useContentTests, useDeleteContentTest } from '@/entities/content-test';
import { usePermissions, useCurrentUserId } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import { ROUTES } from '@/shared/config/routes';
import { SectionLayout } from '@/shared/ui/layout';
import { ToolBadge } from '@/shared/ui/ToolBadge';
import { ContentTestStatusChip } from './components/ContentTestStatusChip';
import { useTranslation } from 'react-i18next';
import { i18n } from '@/shared/i18n';

const PAGE_SIZE = 10;

const SKELETON_ROWS = 5;

// Owner names are resolved org-wide by the backend (so org admins / non-members
// resolve too); the frontend only decides whether the owner is the current user.
const toOwnerInfo = (
  test: ContentTestListItemDto,
  currentUserId: string | undefined,
): OwnerInfo => {
  // `owner` is typed as required, but an older backend (deploy/rollout window)
  // may omit it — guard so the list never crashes on a missing field.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const owner = test.owner ?? {
    id: test.createdBy,
    name: i18n.t('contentTest:contentTest.overview.unknown'),
    avatarUrl: null,
  };
  return {
    name: owner.name,
    avatarUrl: owner.avatarUrl,
    isMe: owner.id === currentUserId,
  };
};

const formatScore = (raw: unknown): number | null => {
  if (typeof raw === 'number') return Math.round(raw);
  if (typeof raw === 'string' && raw.trim() !== '' && !Number.isNaN(Number(raw))) {
    return Math.round(Number(raw));
  }
  return null;
};

interface RowMenuProps {
  test: ContentTestListItemDto;
  onDelete: (test: ContentTestListItemDto) => void;
}

const RowMenu = ({ test, onDelete }: RowMenuProps) => {
  const { t } = useTranslation('contentTest');
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="mini"
          className="h-8 w-8 p-0"
          aria-label={t('contentTest.overview.actionsFor', { name: test.name })}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical size={18} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onSelect={() => onDelete(test)}>
          {t('contentTest.overview.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const OverviewInternal = ({ workspaceId }: { workspaceId: string }) => {
  const { t } = useTranslation('contentTest');

  const navigate = useNavigate();
  const { canAccessTestContent } = usePermissions();

  // Content Test is reached from the Tools hub, so the back button returns there.
  const backTarget = ROUTES.TOOLS_HUB;
  const [page, setPage] = useState(1);
  const [pendingDelete, setPendingDelete] = useState<ContentTestListItemDto | null>(null);

  const { data, isPending } = useContentTests(workspaceId, { page, pageSize: PAGE_SIZE });
  const deleteMutation = useDeleteContentTest(workspaceId);

  const pageItems = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageCount = total > 0 ? Math.ceil(total / PAGE_SIZE) : 0;

  const currentUserId = useCurrentUserId();

  useEffect(() => {
    if (pageCount > 0 && page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount]);

  const handleNew = () => void navigate(ROUTES.TOOLS_CONTENT_TEST_NEW);

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteMutation.mutateAsync(pendingDelete.id);
      notificationService.showSuccess(t('contentTest.overview.contentTestDeleted'));
    } catch {
      notificationService.showError(t('contentTest.overview.failedToDeleteContentTest'));
    } finally {
      setPendingDelete(null);
    }
  };

  const columnCount = canAccessTestContent ? 5 : 4;
  const showInitialEmptyState = !isPending && total === 0;
  const showToolbar = !isPending && !showInitialEmptyState;

  return (
    <div className="h-full overflow-y-auto px-4 py-4">
      <SectionLayout
        variant="detail"
        title={t('contentTest.overview.contentTest')}
        subtitle={t('contentTest.overview.evaluateConceptsAgainstPersonasAnd')}
        icon={
          // eslint-disable-next-line i18next/no-literal-string -- a category id, not copy
          <ToolBadge toolId="content-test" category="plan" icon={ChartColumnIncreasing} size="xl" />
        }
        onBack={() => void navigate(backTarget)}
      >
        <div className="flex flex-1 flex-col gap-3 overflow-hidden rounded-lg bg-card">
          {isPending && <ToolbarSkeleton showButton={canAccessTestContent} />}
          {showToolbar && (
            <ContentToolbar
              title={t('contentTest.overview.contentTest')}
              itemCount={total}
              createButton={
                canAccessTestContent
                  ? {
                      label: t('contentTest.overview.newContentTest'),
                      onClick: handleNew,
                    }
                  : undefined
              }
            />
          )}

          {showInitialEmptyState ? (
            <EmptyState
              icon={<ChartColumnIncreasing className="size-6" />}
              title={t('contentTest.overview.startTestingYourContent')}
              description={t('contentTest.overview.addYourFirstConceptTo')}
            >
              {canAccessTestContent && (
                <Button variant="outline" onClick={handleNew}>
                  <Plus className="size-4" />
                  {t('contentTest.overview.createContentTest')}
                </Button>
              )}
            </EmptyState>
          ) : (
            <>
              <Datatable className="table-fixed **:data-[slot=datatable-cell]:py-2.5 **:data-[slot=datatable-cell]:text-sm **:data-[slot=datatable-cell]:font-normal **:data-[slot=datatable-head]:text-sm **:data-[slot=datatable-head]:font-medium px-6">
                <DatatableHeader>
                  <DatatableRow>
                    <DatatableHead className="text-muted-foreground font-medium">
                      {t('contentTest.overview.name')}
                    </DatatableHead>
                    <DatatableHead className="text-muted-foreground font-medium w-[140px]">
                      {t('contentTest.overview.score')}
                    </DatatableHead>
                    <DatatableHead className="text-muted-foreground font-medium w-[160px]">
                      {t('contentTest.overview.setup')}
                    </DatatableHead>
                    <DatatableHead className="text-muted-foreground font-medium w-[200px]">
                      {t('contentTest.overview.owner')}
                    </DatatableHead>
                    {canAccessTestContent && (
                      <DatatableHead
                        className="w-12"
                        aria-label={t('contentTest.overview.actions')}
                      />
                    )}
                  </DatatableRow>
                </DatatableHeader>
                <DatatableBody>
                  {isPending &&
                    // single call site — inlined directly here rather
                    // than a `renderSkeleton`-style closure.
                    Array.from({ length: SKELETON_ROWS }).map((_, rowIndex) => (
                      <DatatableRow key={`skeleton-${rowIndex}`}>
                        {Array.from({ length: columnCount }).map((_, colIndex) => (
                          <DatatableCell key={`skeleton-cell-${colIndex}`}>
                            <Skeleton className="h-4 w-3/4" />
                          </DatatableCell>
                        ))}
                      </DatatableRow>
                    ))}
                  {!isPending &&
                    pageItems.map((test) => {
                      const score = formatScore(test.overallScore);
                      return (
                        <DatatableRow
                          key={test.id}
                          interactive
                          onClick={() => void navigate(ROUTES.TOOLS_CONTENT_TEST_DETAIL(test.id))}
                          className="cursor-pointer"
                        >
                          <DatatableCell>
                            <span className="text-foreground">{test.name}</span>
                          </DatatableCell>
                          <DatatableCell>
                            {score !== null ? (
                              <span className="inline-flex items-center gap-1.5">
                                <TrendingUp size={16} aria-hidden />
                                {score}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </DatatableCell>
                          <DatatableCell>
                            <ContentTestStatusChip status={test.status} />
                          </DatatableCell>
                          <DatatableCell>
                            <OwnerCell owner={toOwnerInfo(test, currentUserId)} />
                          </DatatableCell>
                          {canAccessTestContent && (
                            <DatatableCell>
                              <RowMenu test={test} onDelete={setPendingDelete} />
                            </DatatableCell>
                          )}
                        </DatatableRow>
                      );
                    })}
                </DatatableBody>
              </Datatable>

              <PaginationControls
                page={page}
                totalItems={total}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
                className="py-4"
              />
            </>
          )}
        </div>
      </SectionLayout>

      <DeleteConfirmationModal
        open={!!pendingDelete}
        isDeleting={deleteMutation.isPending}
        resourceType={t('contentTest.overview.contentTestResource')}
        resourceName={pendingDelete?.name}
        warningMessage={t('contentTest.overview.thisContentTestAndIts')}
        onCancel={() => setPendingDelete(null)}
        onConfirmDelete={() => void handleConfirmDelete()}
      />
    </div>
  );
};

export const Overview = () => {
  const workspaceId = useCurrentWorkspaceId();

  if (!workspaceId) return <Loading />;

  return <OverviewInternal workspaceId={workspaceId} />;
};

Overview.displayName = 'Overview';
